import { useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

import type { DragReorder } from '../util/useDragReorder.js';

/**
 * Native drag paints its own snapshot of the source, and Chrome misplaces a
 * sticky header's; pointer events let the page draw the carried label where
 * the pointer is.
 */
export interface ColumnDrag {
  /** The column being carried, for the ghost's label. */
  carried: string | null;
  /** The ghost element: moved by style on every pointer move, never by a render. */
  ghost: RefObject<HTMLDivElement | null>;
  start: (key: string) => (event: ReactPointerEvent<HTMLElement>) => void;
  stateOf: (key: string) => ReturnType<DragReorder['rowState']>;
}

/** How far the pointer travels before a press becomes a drag. */
const THRESHOLD = 4;
const GHOST_OFFSET = 12;

/** Reorders `keys` by dragging headers marked `data-column-key` within one row.
 *  The table re-renders only when the column under the pointer changes. */
export function useColumnDrag(
  keys: string[],
  move: (from: string, to: string) => void,
): ColumnDrag {
  const [drag, setDrag] = useState<{ key: string; over: string | null } | null>(null);
  const ghost = useRef<HTMLDivElement>(null);

  const start = (key: string) => (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const origin = { x: event.clientX, y: event.clientY };
    const spans = [
      ...(event.currentTarget.closest('tr')?.querySelectorAll<HTMLElement>('[data-column-key]') ??
        []),
    ]
      .map((cell) => ({ key: cell.dataset.columnKey!, rect: cell.getBoundingClientRect() }))
      .filter((span) => keys.includes(span.key));
    const overAt = (x: number) =>
      spans.find(({ rect }) => x >= rect.left && x < rect.right)?.key ?? null;
    let live = false;
    let over: string | null = null;

    const place = (x: number, y: number) => {
      if (ghost.current) {
        ghost.current.style.transform = `translate(${x + GHOST_OFFSET}px, ${y + GHOST_OFFSET}px)`;
      }
    };
    const onMove = (moved: PointerEvent) => {
      if (!live && Math.hypot(moved.clientX - origin.x, moved.clientY - origin.y) < THRESHOLD)
        return;
      const next = overAt(moved.clientX);
      if (!live || next !== over) {
        live = true;
        over = next;
        setDrag({ key, over });
      }
      place(moved.clientX, moved.clientY);
      requestAnimationFrame(() => place(moved.clientX, moved.clientY));
    };
    const finish = (ended: PointerEvent) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      const target = live && ended.type === 'pointerup' ? overAt(ended.clientX) : null;
      if (target && target !== key) move(key, target);
      setDrag(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  };

  return {
    carried: drag?.key ?? null,
    ghost,
    start,
    stateOf: (key) => {
      if (!drag) return null;
      if (drag.key === key) return 'dragging';
      if (drag.over !== key) return null;
      return keys.indexOf(drag.key) < keys.indexOf(key) ? 'overAfter' : 'overBefore';
    },
  };
}
